/**
 * Badge — shared label primitive.
 *
 * Replaces the inline `<span className="inline-block bg-gray-100 ...">` division
 * badge pattern duplicated across ResultCard, SchoolDetailPage, and WatchlistPage.
 *
 * Design system tokens:
 *   - neutral: surfaceCard (bg-white), borderSubtle (border-gray-200),
 *              textSecondary (text-gray-700), typeLabel (text-xs font-semibold tracking-wide)
 *   - tier variants available for future score-tier pill usage
 *
 * No Radix dependency — a <span> is correct for a non-interactive label.
 */

import React from 'react';

type Variant = 'neutral' | 'tier-high' | 'tier-med' | 'tier-low';

interface Props {
  variant?: Variant;
  children: React.ReactNode;
  className?: string;
}

const variantClasses: Record<Variant, string> = {
  // Pattern 4B (00-style-refresh-references.md): neutral badge uses surface-sunken
  // background — recedes against the card white while school name advances.
  // Border updates from border-gray-200 to border-hairline for consistency.
  neutral:    'bg-surface-sunken text-gray-700 border border-hairline',
  'tier-high':'bg-green-50  text-green-700  border border-green-200',
  'tier-med': 'bg-amber-50  text-amber-700  border border-amber-200',
  'tier-low': 'bg-red-50    text-red-600    border border-red-200',
};

export function Badge({ variant = 'neutral', children, className = '' }: Props) {
  return (
    <span
      className={`inline-block text-xs font-semibold tracking-wide px-2 py-0.5 rounded ${variantClasses[variant]} ${className}`}
    >
      {children}
    </span>
  );
}
